import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Upload, Image as ImageIcon, Check, ChevronRight, ChevronLeft,
  Download, FileSpreadsheet, Store, MapPin, Phone, Mail, FileText,
  User, CreditCard, AlertCircle, Loader2, X, Eye, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import GoogleMapsLocationPicker from '../components/GoogleMapsLocationPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingWizard = ({ user, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Step 1: Business Profile
  const [businessProfile, setBusinessProfile] = useState({
    brand_name: '',
    owner_name: user?.full_name || '',
    company_name: user?.company_name || user?.business_name || '',
    gstin: user?.gst_number || '',
    pincode: '',
    country: '',
    state: '',
    district: '',
    city: '',
    company_legal_address: '',
    contact_email: user?.email || '',
    contact_phone: user?.phone || '',
    logo_url: null
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [pincodeValidating, setPincodeValidating] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [pincodeValid, setPincodeValid] = useState(false);
  
  // Step 2: Menu Setup
  const [menuMethod, setMenuMethod] = useState('manual'); // 'manual' or 'excel'
  const [menuItems, setMenuItems] = useState([]);
  const [newMenuItem, setNewMenuItem] = useState({
    category: '',
    name: '',
    price: '',
    description: '',
    is_veg: true,
    is_available: true
  });
  
  // Step 3: Franchise Setup
  // Step 3: Franchise Setup - New Structure
  const [franchiseData, setFranchiseData] = useState({
    // Section 1: Franchise Details
    outlet_name: '',
    outlet_pincode: '',
    outlet_state: '',
    outlet_district: '',
    outlet_city: '',
    outlet_full_address: '',
    outlet_location: { lat: null, lng: null },
    rent_agreement_doc: null,
    is_gst_applicable: false,
    outlet_gstin: '',
    
    // Section 2: Owner/Incharge Details
    owner_full_name: '',
    owner_email: '',
    owner_phone: '',
    owner_full_address: '',
    owner_id_proof_type: '',
    owner_id_proof_doc: null,
    owner_photo: null,
    
    // Section 3: Documents (optional)
    franchise_agreement_doc: null,
    fssai_license_doc: null,
    trade_license_doc: null,
    gst_certificate_doc: null
  });
  
  // Franchise pincode validation state
  const [franchisePincodeValidating, setFranchisePincodeValidating] = useState(false);
  const [franchisePincodeError, setFranchisePincodeError] = useState('');
  const [franchisePincodeValid, setFranchisePincodeValid] = useState(false);
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(null);
  
  const [franchiseDocuments, setFranchiseDocuments] = useState({});
  const [createdFranchise, setCreatedFranchise] = useState(null);
  
  // Master data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [franchiseStates, setFranchiseStates] = useState([]);
  const [franchiseCities, setFranchiseCities] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  // Fetch countries on mount
  useEffect(() => {
    fetchCountries();
    fetchOnboardingStatus();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (businessProfile.country) {
      fetchStates(businessProfile.country);
    }
  }, [businessProfile.country]);

  // Fetch cities when state changes
  useEffect(() => {
    if (businessProfile.state) {
      fetchCities(businessProfile.state);
    }
  }, [businessProfile.state]);

  // Fetch franchise states
  useEffect(() => {
    if (franchiseData.state) {
      fetchFranchiseCities(franchiseData.state);
    }
  }, [franchiseData.state]);

  // Fetch menu items when on Step 2 (Menu Setup) or when component mounts
  useEffect(() => {
    if (currentStep === 2) {
      fetchMenuItems();
    }
  }, [currentStep]);

  const fetchOnboardingStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      
      if (data.success) {
        // Resume from last step
        if (data.current_step > 0 && data.current_step < 4) {
          setCurrentStep(data.current_step + 1);
        }
        
        // Restore business profile if available
        if (data.business_profile) {
          setBusinessProfile(prev => ({
            ...prev,
            ...data.business_profile
          }));
          if (data.business_profile.logo_url) {
            setLogoPreview(`${API_URL}${data.business_profile.logo_url}`);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/master/countries`);
      const data = await response.json();
      if (data.success) {
        setCountries(data.countries);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async (countryCode) => {
    try {
      const response = await fetch(`${API_URL}/api/master/states/${countryCode}`);
      const data = await response.json();
      if (data.success) {
        setStates(data.states);
        setFranchiseStates(data.states); // Also set for franchise
      }
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchCities = async (stateCode) => {
    try {
      const response = await fetch(`${API_URL}/api/master/cities/${stateCode}`);
      const data = await response.json();
      if (data.success) {
        setCities(data.cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchFranchiseCities = async (stateCode) => {
    try {
      const response = await fetch(`${API_URL}/api/master/cities/${stateCode}`);
      const data = await response.json();
      if (data.success) {
        setFranchiseCities(data.cities);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  // Validate pincode and fetch location details
  const validateAndFetchPincodeDetails = async (pincode) => {
    if (!pincode || pincode.length !== 6) {
      setPincodeError('Pincode must be 6 digits');
      setPincodeValid(false);
      setBusinessProfile(prev => ({
        ...prev,
        country: '',
        state: '',
        district: ''
      }));
      return;
    }

    setPincodeValidating(true);
    setPincodeError('');
    
    try {
      // Use India Post API to validate pincode
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      
      if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
        const postOffice = data[0].PostOffice[0];
        
        setPincodeValid(true);
        setPincodeError('');
        setBusinessProfile(prev => ({
          ...prev,
          country: 'India',
          state: postOffice.State || '',
          district: postOffice.District || ''
        }));
        
        toast.success(`Location found: ${postOffice.District}, ${postOffice.State}`);
      } else {
        setPincodeValid(false);
        setPincodeError('Invalid pincode. Please enter a valid Indian pincode.');
        setBusinessProfile(prev => ({
          ...prev,
          country: '',
          state: '',
          district: ''
        }));
      }
    } catch (error) {
      console.error('Error validating pincode:', error);
      setPincodeError('Unable to validate pincode. Please try again.');
      setPincodeValid(false);
    } finally {
      setPincodeValidating(false);
    }
  };

  // Handle pincode input change with debounce
  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only digits, max 6
    setBusinessProfile(prev => ({ ...prev, pincode: value }));
    
    // Reset validation state when typing
    if (value.length < 6) {
      setPincodeError('');
      setPincodeValid(false);
      setBusinessProfile(prev => ({
        ...prev,
        pincode: value,
        country: '',
        state: '',
        district: ''
      }));
    }
    
    // Auto-validate when 6 digits are entered
    if (value.length === 6) {
      validateAndFetchPincodeDetails(value);
    }
  };

  // Validate franchise pincode and fetch location details
  const validateFranchisePincode = async (pincode) => {
    if (!pincode || pincode.length !== 6) {
      setFranchisePincodeError('Pincode must be 6 digits');
      setFranchisePincodeValid(false);
      setFranchiseData(prev => ({
        ...prev,
        outlet_state: '',
        outlet_district: ''
      }));
      return;
    }

    setFranchisePincodeValidating(true);
    setFranchisePincodeError('');
    
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      
      if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
        const postOffice = data[0].PostOffice[0];
        
        setFranchisePincodeValid(true);
        setFranchisePincodeError('');
        setFranchiseData(prev => ({
          ...prev,
          outlet_state: postOffice.State || '',
          outlet_district: postOffice.District || ''
        }));
        
        toast.success(`Location found: ${postOffice.District}, ${postOffice.State}`);
      } else {
        setFranchisePincodeValid(false);
        setFranchisePincodeError('Invalid pincode. Please enter a valid Indian pincode.');
        setFranchiseData(prev => ({
          ...prev,
          outlet_state: '',
          outlet_district: ''
        }));
      }
    } catch (error) {
      console.error('Error validating franchise pincode:', error);
      setFranchisePincodeError('Unable to validate pincode. Please try again.');
      setFranchisePincodeValid(false);
    } finally {
      setFranchisePincodeValidating(false);
    }
  };

  // Handle franchise pincode input change
  const handleFranchisePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setFranchiseData(prev => ({ ...prev, outlet_pincode: value }));
    
    if (value.length < 6) {
      setFranchisePincodeError('');
      setFranchisePincodeValid(false);
      setFranchiseData(prev => ({
        ...prev,
        outlet_pincode: value,
        outlet_state: '',
        outlet_district: ''
      }));
    }
    
    if (value.length === 6) {
      validateFranchisePincode(value);
    }
  };

  // Handle file upload for franchise documents
  const handleFranchiseFileUpload = async (file, fieldName, maxSizeMB = 10) => {
    if (!file) return null;
    
    // Check file size (10MB max)
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File size must be less than ${maxSizeMB}MB`);
      return null;
    }
    
    // Convert to base64
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result;
        setFranchiseData(prev => ({
          ...prev,
          [fieldName]: {
            name: file.name,
            type: file.type,
            size: file.size,
            data: base64
          }
        }));
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Logo upload handler
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Only enforce file size limit (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo must be under 5MB');
      return;
    }

    // Accept any image type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Preview without dimension validation
    const reader = new FileReader();
    reader.onload = (e) => {
      setLogoPreview(e.target.result);
      setLogoFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Upload logo to server
  const uploadLogo = async () => {
    if (!logoFile) return null;

    const formData = new FormData();
    formData.append('logo', logoFile); // Key must match backend param name

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // DO NOT set Content-Type for FormData - browser sets it with boundary
        },
        body: formData
      });

      // Read response body only once
      const data = await response.json();
      
      if (!response.ok) {
        // Handle error response (422, 400, etc.)
        const errorMessage = data.detail || data.error || 'Failed to upload logo';
        toast.error(errorMessage);
        return null;
      }
      
      if (data.success) {
        return data.logo_url;
      } else {
        toast.error(data.error || 'Failed to upload logo');
        return null;
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo. Please try again.');
      return null;
    }
  };

  // Save Business Profile (Step 1)
  const saveBusinessProfile = async () => {
    setSaving(true);
    try {
      // Upload logo if selected
      let logoUrl = businessProfile.logo_url;
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl && logoFile) {
          setSaving(false);
          return false;
        }
      }

      const profileData = {
        ...businessProfile,
        logo_url: logoUrl
      };

      const response = await fetch(`${API_URL}/api/onboarding/business-profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(profileData)
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Business profile saved!');
        setBusinessProfile(prev => ({ ...prev, logo_url: logoUrl }));
        return true;
      } else {
        toast.error(data.error || 'Failed to save profile');
        return false;
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Download menu template
  const downloadMenuTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/menu-template`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FOS_Menu_Template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  // Upload menu Excel
  const uploadMenuExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/menu-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Imported ${data.imported_count} menu items!`);
        fetchMenuItems();
      } else {
        toast.error(data.error || data.message || 'Failed to import menu');
      }
    } catch (error) {
      console.error('Error uploading menu:', error);
      toast.error('Failed to upload menu');
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing menu items
  const fetchMenuItems = async () => {
    try {
      const response = await fetch(`${API_URL}/api/menu-items`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      // API returns {menu_items: [...]} - extract the array
      if (data.menu_items && Array.isArray(data.menu_items)) {
        setMenuItems(data.menu_items);
      } else if (Array.isArray(data)) {
        // Fallback for direct array response
        setMenuItems(data);
      }
    } catch (error) {
      console.error('Error fetching menu items:', error);
    }
  };

  // Add menu item manually
  const addMenuItem = async () => {
    if (!newMenuItem.category || !newMenuItem.name || !newMenuItem.price) {
      toast.error('Category, name, and price are required');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/menu-items`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...newMenuItem,
          price: parseFloat(newMenuItem.price)
        })
      });

      const data = await response.json();
      
      if (data.id || data._id) {
        toast.success('Menu item added!');
        setMenuItems(prev => [...prev, { ...newMenuItem, id: data.id || data._id }]);
        setNewMenuItem({
          category: newMenuItem.category, // Keep category for convenience
          name: '',
          price: '',
          description: '',
          is_veg: true,
          is_available: true
        });
      } else {
        toast.error(data.error || 'Failed to add menu item');
      }
    } catch (error) {
      console.error('Error adding menu item:', error);
      toast.error('Failed to add menu item');
    } finally {
      setLoading(false);
    }
  };

  // Complete menu setup
  const completeMenuSetup = async (skipped = false) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/menu-setup/complete?skipped=${skipped}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      const data = await response.json();
      
      if (data.success) {
        return true;
      } else {
        toast.error(data.error || 'Failed to complete menu setup');
        return false;
      }
    } catch (error) {
      console.error('Error completing menu setup:', error);
      toast.error('Failed to complete menu setup');
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Upload franchise document
  const uploadDocument = async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/onboarding/upload-document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      
      if (data.success) {
        setFranchiseDocuments(prev => ({
          ...prev,
          [documentType]: data.document_url
        }));
        toast.success(`${documentType.replace('_', ' ')} uploaded!`);
        return data.document_url;
      } else {
        toast.error(data.error || 'Failed to upload document');
        return null;
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Failed to upload document');
      return null;
    }
  };

  // Create franchise
  const createFranchise = async () => {
    setSaving(true);
    try {
      const franchisePayload = {
        ...franchiseData,
        ...franchiseDocuments
      };

      // Log the payload being sent to the API
      console.log('=== FRANCHISE CREATION PAYLOAD ===');
      console.log(JSON.stringify(franchisePayload, null, 2));
      console.log('=================================');

      const response = await fetch(`${API_URL}/api/onboarding/franchise`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(franchisePayload)
      });

      // Try to parse response as JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        toast.error(`Server error (${response.status}). Please try again.`);
        return null;
      }
      
      if (!response.ok) {
        const errorMessage = data.detail || data.error || 'Failed to create franchise';
        toast.error(errorMessage);
        return null;
      }
      
      if (data.success) {
        toast.success('Franchise created!');
        setCreatedFranchise(data);
        return data;
      } else {
        toast.error(data.error || 'Failed to create franchise');
        return null;
      }
    } catch (error) {
      console.error('Error creating franchise:', error);
      toast.error('Failed to create franchise. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Save franchise as draft
  const saveFranchiseDraft = async () => {
    if (!createdFranchise?.franchise_id) {
      // Create first
      const result = await createFranchise();
      if (result) {
        toast.success('Franchise saved as draft!');
      }
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/franchise/${createdFranchise.franchise_id}/draft`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...franchiseData,
          ...franchiseDocuments
        })
      });

      // Try to parse response as JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        toast.error(`Server error (${response.status}). Please try again.`);
        return;
      }
      
      if (!response.ok) {
        const errorMessage = data.detail || data.error || 'Failed to save draft';
        toast.error(errorMessage);
        return;
      }
      
      if (data.success) {
        toast.success('Draft saved!');
      } else {
        toast.error(data.error || 'Failed to save draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Go Live
  const goLive = async () => {
    // Check if menu items exist
    if (menuItems.length === 0) {
      toast.error('Please add at least 1 menu item before going live');
      return;
    }

    // Create franchise if not created
    let franchiseId = createdFranchise?.franchise_id;
    if (!franchiseId) {
      const result = await createFranchise();
      if (!result) return;
      franchiseId = result.franchise_id;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/franchise/${franchiseId}/go-live`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      // Try to parse response as JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        toast.error(`Server error (${response.status}). Please try again.`);
        return;
      }
      
      if (!response.ok) {
        const errorMessage = data.detail || data.error || 'Failed to go live';
        toast.error(errorMessage);
        return;
      }
      
      if (data.success) {
        toast.success('🎉 Congratulations! Your franchise is now live!');
        // Show email notification if sent
        if (data.email_sent && data.email_address) {
          setTimeout(() => {
            toast.success(`📧 Login credentials sent to ${data.email_address}`);
          }, 1500);
        }
        onComplete();
      } else {
        toast.error(data.error || 'Failed to go live');
      }
    } catch (error) {
      console.error('Error going live:', error);
      toast.error('Failed to go live. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Validation helpers
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
    return phone.length >= 10 && phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateGSTIN = (gstin) => {
    if (!gstin) return true; // GSTIN is optional
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  const validatePincode = (pincode) => {
    return pincode && pincode.length === 6 && /^\d{6}$/.test(pincode);
  };

  // Navigation
  const nextStep = async () => {
    if (currentStep === 1) {
      // Validate business profile
      const errors = [];
      
      if (!businessProfile.brand_name?.trim()) errors.push('Brand name is required');
      if (!businessProfile.company_name?.trim()) errors.push('Company name is required');
      if (!businessProfile.pincode?.trim() || businessProfile.pincode.length !== 6) errors.push('Valid 6-digit pincode is required');
      if (!pincodeValid) errors.push('Please enter a valid pincode to auto-fill location details');
      if (!businessProfile.state?.trim()) errors.push('State is required (auto-filled from pincode)');
      if (!businessProfile.district?.trim()) errors.push('District is required (auto-filled from pincode)');
      if (!businessProfile.city?.trim()) errors.push('City is required');
      if (!businessProfile.company_legal_address?.trim()) errors.push('Company Legal Address is required');
      
      if (businessProfile.contact_email && !validateEmail(businessProfile.contact_email)) {
        errors.push('Invalid email format');
      }
      if (businessProfile.contact_phone && !validatePhone(businessProfile.contact_phone)) {
        errors.push('Invalid phone number (min 10 digits)');
      }
      if (businessProfile.gstin && !validateGSTIN(businessProfile.gstin)) {
        errors.push('Invalid GSTIN format');
      }
      
      if (errors.length > 0) {
        toast.error(errors[0]); // Show first error
        return;
      }
      
      const saved = await saveBusinessProfile();
      if (!saved) return;
    } else if (currentStep === 2) {
      await completeMenuSetup(menuItems.length === 0);
    } else if (currentStep === 3) {
      // Validate franchise data before creating
      const errors = [];
      
      // Section 1: Franchise Details
      if (!franchiseData.outlet_name?.trim()) errors.push('Outlet name is required');
      if (!franchiseData.outlet_pincode?.trim() || franchiseData.outlet_pincode.length !== 6) {
        errors.push('Valid 6-digit pincode is required');
      }
      if (!franchisePincodeValid) errors.push('Please enter a valid pincode');
      if (!franchiseData.outlet_city?.trim()) errors.push('City is required');
      if (!franchiseData.outlet_full_address?.trim()) errors.push('Full address is required');
      
      if (franchiseData.is_gst_applicable && !franchiseData.outlet_gstin?.trim()) {
        errors.push('GSTIN is required when GST is applicable');
      }
      
      // Section 2: Owner Details
      if (!franchiseData.owner_full_name?.trim()) errors.push('Owner full name is required');
      if (!franchiseData.owner_email?.trim()) errors.push('Owner email is required');
      if (franchiseData.owner_email && !validateEmail(franchiseData.owner_email)) {
        errors.push('Invalid owner email format');
      }
      if (!franchiseData.owner_phone?.trim()) errors.push('Owner phone is required');
      if (franchiseData.owner_phone && !validatePhone(franchiseData.owner_phone)) {
        errors.push('Invalid owner phone number');
      }
      if (!franchiseData.owner_full_address?.trim()) errors.push('Owner address is required');
      if (!franchiseData.owner_id_proof_type) errors.push('Identity proof type is required');
      if (!franchiseData.owner_id_proof_doc) errors.push('Identity proof document is required');
      if (!franchiseData.owner_photo) errors.push('Owner photo is required');
      
      if (errors.length > 0) {
        toast.error(errors[0]);
        return;
      }
      
      // Create the franchise
      const result = await createFranchise();
      if (!result) return; // Don't proceed if creation failed
    }
    
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const skipMenuSetup = async () => {
    await completeMenuSetup(true);
    setCurrentStep(3);
  };

  // Render step progress
  const renderProgress = () => {
    const steps = [
      { num: 1, label: 'Business Profile', icon: Building2 },
      { num: 2, label: 'Menu Setup', icon: FileSpreadsheet },
      { num: 3, label: 'First Franchise', icon: Store },
      { num: 4, label: 'Go Live', icon: Check }
    ];

    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          {steps.map((step, index) => (
            <React.Fragment key={step.num}>
              <div 
                className={`flex flex-col items-center ${
                  currentStep >= step.num ? 'text-amber-600' : 'text-gray-400'
                }`}
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                    currentStep >= step.num 
                      ? 'bg-amber-600 border-amber-600 text-white' 
                      : 'border-gray-300 text-gray-400'
                  }`}
                >
                  <step.icon className="w-5 h-5" />
                </div>
                <span className="text-xs mt-1 hidden sm:block">{step.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div 
                  className={`flex-1 h-1 mx-2 rounded ${
                    currentStep > step.num ? 'bg-amber-600' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <Progress value={(currentStep / 4) * 100} className="h-2" />
      </div>
    );
  };

  // Step 1: Business Profile
  const renderBusinessProfile = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Set Up Your Business Profile</h2>
        <p className="text-gray-600 mt-2">Tell us about your business</p>
      </div>

      {/* Logo Upload */}
      <div className="flex flex-col items-center gap-4 p-6 border-2 border-dashed rounded-lg">
        {logoPreview ? (
          <div className="relative">
            <img 
              src={logoPreview} 
              alt="Logo preview" 
              className="w-32 h-32 object-contain rounded-lg border"
            />
            <button
              onClick={() => { setLogoPreview(null); setLogoFile(null); }}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
            <ImageIcon className="w-12 h-12 text-gray-400" />
          </div>
        )}
        <div className="text-center">
          <Label htmlFor="logo-upload" className="cursor-pointer">
            <span className="text-amber-600 hover:underline font-medium">Upload Logo</span>
          </Label>
          <div className="text-xs text-gray-500 mt-2 space-y-0.5">
            <p>Max size: 5MB</p>
            <p className="text-gray-400">Recommended: PNG, 512x512 or 1024x1024, square</p>
          </div>
          <Input
            id="logo-upload"
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Owner Name - from registration */}
      <div>
        <Label htmlFor="owner_name">Owner/Contact Name {user?.full_name && <span className="text-xs text-green-600 font-medium">(from registration - cannot be changed)</span>}</Label>
        <Input
          id="owner_name"
          value={businessProfile.owner_name}
          onChange={(e) => setBusinessProfile(prev => ({ ...prev, owner_name: e.target.value }))}
          placeholder="Full name"
          disabled={!!user?.full_name}
          className={user?.full_name ? "bg-muted cursor-not-allowed" : ""}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label htmlFor="brand_name">Brand Name *</Label>
            {businessProfile.company_name && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="same_as_company"
                  checked={businessProfile.brand_name === businessProfile.company_name}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setBusinessProfile(prev => ({ ...prev, brand_name: prev.company_name }));
                    } else {
                      setBusinessProfile(prev => ({ ...prev, brand_name: '' }));
                    }
                  }}
                />
                <Label htmlFor="same_as_company" className="text-xs text-muted-foreground cursor-pointer">
                  Same as company name
                </Label>
              </div>
            )}
          </div>
          <Input
            id="brand_name"
            value={businessProfile.brand_name}
            onChange={(e) => setBusinessProfile(prev => ({ ...prev, brand_name: e.target.value }))}
            placeholder="Your brand name"
            disabled={businessProfile.brand_name === businessProfile.company_name && businessProfile.company_name !== ''}
            className={businessProfile.brand_name === businessProfile.company_name && businessProfile.company_name !== '' ? "bg-muted" : ""}
          />
        </div>
        <div>
          <Label htmlFor="company_name">Company Name * {(user?.company_name || user?.business_name) && <span className="text-xs text-green-600 font-medium">(from registration - cannot be changed)</span>}</Label>
          <Input
            id="company_name"
            value={businessProfile.company_name}
            onChange={(e) => setBusinessProfile(prev => ({ ...prev, company_name: e.target.value }))}
            placeholder="Legal company name"
            disabled={!!(user?.company_name || user?.business_name)}
            className={(user?.company_name || user?.business_name) ? "bg-muted cursor-not-allowed" : ""}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="gstin">GSTIN</Label>
        <Input
          id="gstin"
          value={businessProfile.gstin}
          onChange={(e) => setBusinessProfile(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
          placeholder="22AAAAA0000A1Z5"
          maxLength={15}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="pincode">Pincode * <span className="text-xs text-muted-foreground">(Enter pincode first)</span></Label>
          <div className="relative">
            <Input
              id="pincode"
              value={businessProfile.pincode}
              onChange={handlePincodeChange}
              placeholder="560001"
              maxLength={6}
              className={`${pincodeError ? 'border-red-500 focus:border-red-500' : ''} ${pincodeValid ? 'border-green-500' : ''}`}
            />
            {pincodeValidating && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {pincodeValid && !pincodeValidating && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <CheckCircle className="h-4 w-4 text-green-500" />
              </div>
            )}
          </div>
          {pincodeError && <p className="text-xs text-red-500 mt-1">{pincodeError}</p>}
        </div>
        <div>
          <Label>Country <span className="text-xs text-green-600 font-medium">(auto-filled)</span></Label>
          <Input
            value={businessProfile.country}
            placeholder="Auto-filled from pincode"
            disabled
            className="bg-muted cursor-not-allowed"
          />
        </div>
        <div>
          <Label>State <span className="text-xs text-green-600 font-medium">(auto-filled)</span></Label>
          <Input
            value={businessProfile.state}
            placeholder="Auto-filled from pincode"
            disabled
            className="bg-muted cursor-not-allowed"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>District <span className="text-xs text-green-600 font-medium">(auto-filled)</span></Label>
          <Input
            value={businessProfile.district}
            placeholder="Auto-filled from pincode"
            disabled
            className="bg-muted cursor-not-allowed"
          />
        </div>
        <div>
          <Label htmlFor="city">City *</Label>
          <Input
            id="city"
            value={businessProfile.city}
            onChange={(e) => setBusinessProfile(prev => ({ ...prev, city: e.target.value }))}
            placeholder="Enter your city/town name"
            disabled={!pincodeValid}
            className={!pincodeValid ? "bg-muted/50" : ""}
          />
          {!pincodeValid && <p className="text-xs text-muted-foreground mt-1">Enter valid pincode first</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact_phone">Contact Phone {user?.phone && <span className="text-xs text-green-600 font-medium">(from registration - cannot be changed)</span>}</Label>
          <Input
            id="contact_phone"
            value={businessProfile.contact_phone}
            onChange={(e) => setBusinessProfile(prev => ({ ...prev, contact_phone: e.target.value }))}
            placeholder="+91 9876543210"
            disabled={!!user?.phone}
            className={user?.phone ? "bg-muted cursor-not-allowed" : ""}
          />
        </div>
        <div>
          <Label htmlFor="contact_email">Contact Email {user?.email && <span className="text-xs text-green-600 font-medium">(from registration - cannot be changed)</span>}</Label>
          <Input
            id="contact_email"
            type="email"
            value={businessProfile.contact_email}
            onChange={(e) => setBusinessProfile(prev => ({ ...prev, contact_email: e.target.value }))}
            placeholder="business@example.com"
            disabled={!!user?.email}
            className={user?.email ? "bg-muted cursor-not-allowed" : ""}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="company_legal_address">Company Legal Address *</Label>
        <Input
          id="company_legal_address"
          value={businessProfile.company_legal_address}
          onChange={(e) => setBusinessProfile(prev => ({ ...prev, company_legal_address: e.target.value }))}
          placeholder="Enter your company's registered legal address"
        />
      </div>
    </div>
  );

  // Step 2: Menu Setup
  const renderMenuSetup = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Set Up Your Menu</h2>
        <p className="text-gray-600 mt-2">Add your products and categories</p>
      </div>

      <Tabs value={menuMethod} onValueChange={setMenuMethod} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Upload Excel
          </TabsTrigger>
          <TabsTrigger value="manual">
            <FileText className="w-4 h-4 mr-2" />
            Manual Entry
          </TabsTrigger>
        </TabsList>

        <TabsContent value="excel" className="mt-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center gap-4">
                  <Button variant="outline" onClick={downloadMenuTemplate}>
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                <div className="border-2 border-dashed rounded-lg p-8">
                  <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <Label htmlFor="menu-upload" className="cursor-pointer">
                    <span className="text-amber-600 hover:underline text-lg">
                      Click to upload filled template
                    </span>
                    <p className="text-sm text-gray-500 mt-2">
                      Excel file (.xlsx) only
                    </p>
                  </Label>
                  <Input
                    id="menu-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={uploadMenuExcel}
                    className="hidden"
                    disabled={loading}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="manual" className="mt-4">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Input
                    value={newMenuItem.category}
                    onChange={(e) => setNewMenuItem(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Beverages"
                  />
                </div>
                <div>
                  <Label>Item Name *</Label>
                  <Input
                    value={newMenuItem.name}
                    onChange={(e) => setNewMenuItem(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Filter Coffee"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Price (₹) *</Label>
                  <Input
                    type="number"
                    value={newMenuItem.price}
                    onChange={(e) => setNewMenuItem(prev => ({ ...prev, price: e.target.value }))}
                    placeholder="30"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={newMenuItem.description}
                    onChange={(e) => setNewMenuItem(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_veg"
                    checked={newMenuItem.is_veg}
                    onCheckedChange={(checked) => setNewMenuItem(prev => ({ ...prev, is_veg: checked }))}
                  />
                  <Label htmlFor="is_veg">Vegetarian</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_available"
                    checked={newMenuItem.is_available}
                    onCheckedChange={(checked) => setNewMenuItem(prev => ({ ...prev, is_available: checked }))}
                  />
                  <Label htmlFor="is_available">Available</Label>
                </div>
              </div>
              <Button onClick={addMenuItem} disabled={loading} className="w-full">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Add Menu Item
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Menu Items List */}
      {menuItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Added Items ({menuItems.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {menuItems.map((item, index) => (
                <div 
                  key={item.id || index} 
                  className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-gray-500 text-sm ml-2">({item.category})</span>
                  </div>
                  <span className="font-semibold text-amber-600">₹{item.price}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You can skip this step and add menu items later from your dashboard.
        </AlertDescription>
      </Alert>
    </div>
  );

  // Step 3: Franchise Setup (continued in next part due to length)
  const renderFranchiseSetup = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Set Up Your First Franchise</h2>
        <p className="text-gray-600 mt-2">Add your first outlet to get started</p>
      </div>

      {/* Section 1: Franchise Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Store className="w-5 h-5" />
            Franchise Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="outlet_name">Outlet Name *</Label>
            <Input
              id="outlet_name"
              value={franchiseData.outlet_name}
              onChange={(e) => setFranchiseData(prev => ({ ...prev, outlet_name: e.target.value }))}
              placeholder="e.g., Main Branch, Koramangala Outlet"
            />
          </div>

          {/* Outlet Address - Pincode based */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-4">
            <Label className="text-sm font-semibold">Outlet Address *</Label>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="outlet_pincode">Pincode * <span className="text-xs text-muted-foreground">(Enter first)</span></Label>
                <div className="relative">
                  <Input
                    id="outlet_pincode"
                    value={franchiseData.outlet_pincode}
                    onChange={handleFranchisePincodeChange}
                    placeholder="560001"
                    maxLength={6}
                    className={`${franchisePincodeError ? 'border-red-500' : ''} ${franchisePincodeValid ? 'border-green-500' : ''}`}
                  />
                  {franchisePincodeValidating && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {franchisePincodeValid && !franchisePincodeValidating && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </div>
                  )}
                </div>
                {franchisePincodeError && <p className="text-xs text-red-500 mt-1">{franchisePincodeError}</p>}
              </div>
              <div>
                <Label>State <span className="text-xs text-green-600">(auto-filled)</span></Label>
                <Input
                  value={franchiseData.outlet_state}
                  placeholder="Auto-filled"
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
              </div>
              <div>
                <Label>District <span className="text-xs text-green-600">(auto-filled)</span></Label>
                <Input
                  value={franchiseData.outlet_district}
                  placeholder="Auto-filled"
                  disabled
                  className="bg-muted cursor-not-allowed"
                />
              </div>
              <div>
                <Label htmlFor="outlet_city">City *</Label>
                <Input
                  id="outlet_city"
                  value={franchiseData.outlet_city}
                  onChange={(e) => setFranchiseData(prev => ({ ...prev, outlet_city: e.target.value }))}
                  placeholder="Enter city/town"
                  disabled={!franchisePincodeValid}
                  className={!franchisePincodeValid ? "bg-muted/50" : ""}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="outlet_full_address">Full Address *</Label>
            <Input
              id="outlet_full_address"
              value={franchiseData.outlet_full_address}
              onChange={(e) => setFranchiseData(prev => ({ ...prev, outlet_full_address: e.target.value }))}
              placeholder="Complete street address with landmarks"
            />
          </div>

          {/* Google Maps Location Picker */}
          <div>
            <Label>Select Location on Map</Label>
            <div className="mt-2">
              <GoogleMapsLocationPicker
                value={franchiseData.outlet_location}
                onChange={(location) => setFranchiseData(prev => ({ ...prev, outlet_location: location }))}
                pincode={franchiseData.outlet_pincode}
                city={franchiseData.outlet_city}
                height="350px"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              This helps with delivery and navigation to your outlet
            </p>
          </div>

          {/* Rent Agreement Upload */}
          <div>
            <Label>Rent Agreement / Address Proof</Label>
            <div className="flex items-center gap-4 mt-2">
              <Label htmlFor="rent_agreement_upload" className="cursor-pointer">
                <div className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${franchiseData.rent_agreement_doc ? 'border-green-500 bg-green-50' : ''}`}>
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{franchiseData.rent_agreement_doc ? 'Replace File' : 'Upload File'}</span>
                </div>
              </Label>
              <Input
                id="rent_agreement_upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleFranchiseFileUpload(e.target.files[0], 'rent_agreement_doc');
                  }
                }}
              />
              {franchiseData.rent_agreement_doc && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>{franchiseData.rent_agreement_doc.name}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Max file size: 10MB (PDF, JPG, PNG)</p>
          </div>

          {/* GST Checkbox */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_gst_applicable"
                checked={franchiseData.is_gst_applicable}
                onCheckedChange={(checked) => setFranchiseData(prev => ({ 
                  ...prev, 
                  is_gst_applicable: checked,
                  outlet_gstin: checked ? prev.outlet_gstin : ''
                }))}
              />
              <Label htmlFor="is_gst_applicable" className="cursor-pointer">Is this Outlet GST Applicable?</Label>
            </div>
            {franchiseData.is_gst_applicable && (
              <div>
                <Label htmlFor="outlet_gstin">Outlet GSTIN *</Label>
                <Input
                  id="outlet_gstin"
                  value={franchiseData.outlet_gstin}
                  onChange={(e) => setFranchiseData(prev => ({ ...prev, outlet_gstin: e.target.value.toUpperCase() }))}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Owner/Incharge Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            Owner / Incharge Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_full_name">Owner Full Name *</Label>
              <Input
                id="owner_full_name"
                value={franchiseData.owner_full_name}
                onChange={(e) => setFranchiseData(prev => ({ ...prev, owner_full_name: e.target.value }))}
                placeholder="Full name of owner/incharge"
              />
            </div>
            <div>
              <Label htmlFor="owner_phone">Phone Number *</Label>
              <Input
                id="owner_phone"
                value={franchiseData.owner_phone}
                onChange={(e) => setFranchiseData(prev => ({ ...prev, owner_phone: e.target.value }))}
                placeholder="+91 9876543210"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="owner_email">Email ID *</Label>
            <Input
              id="owner_email"
              type="email"
              value={franchiseData.owner_email}
              onChange={(e) => setFranchiseData(prev => ({ ...prev, owner_email: e.target.value }))}
              placeholder="owner@example.com"
            />
          </div>

          <div>
            <Label htmlFor="owner_full_address">Owner Full Address *</Label>
            <Input
              id="owner_full_address"
              value={franchiseData.owner_full_address}
              onChange={(e) => setFranchiseData(prev => ({ ...prev, owner_full_address: e.target.value }))}
              placeholder="Complete residential address"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_id_proof_type">Identity Proof Type *</Label>
              <Select 
                value={franchiseData.owner_id_proof_type}
                onValueChange={(value) => setFranchiseData(prev => ({ ...prev, owner_id_proof_type: value }))}
              >
                <SelectTrigger id="owner_id_proof_type">
                  <SelectValue placeholder="Select ID type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aadhaar">Aadhaar Card</SelectItem>
                  <SelectItem value="pan">PAN Card</SelectItem>
                  <SelectItem value="driving_license">Driving License</SelectItem>
                  <SelectItem value="voter_id">Voter ID</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Upload Identity Proof *</Label>
              <div className="flex items-center gap-2 mt-1">
                <Label htmlFor="owner_id_proof_upload" className="cursor-pointer flex-1">
                  <div className={`flex items-center justify-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${franchiseData.owner_id_proof_doc ? 'border-green-500 bg-green-50' : ''}`}>
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">{franchiseData.owner_id_proof_doc ? franchiseData.owner_id_proof_doc.name : 'Upload'}</span>
                  </div>
                </Label>
                <Input
                  id="owner_id_proof_upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleFranchiseFileUpload(e.target.files[0], 'owner_id_proof_doc');
                    }
                  }}
                />
                {franchiseData.owner_id_proof_doc && <CheckCircle className="w-5 h-5 text-green-500" />}
              </div>
            </div>
          </div>

          <div>
            <Label>Upload Passport Size Photo *</Label>
            <div className="flex items-center gap-4 mt-2">
              <Label htmlFor="owner_photo_upload" className="cursor-pointer">
                <div className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${franchiseData.owner_photo ? 'border-green-500 bg-green-50' : ''}`}>
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{franchiseData.owner_photo ? 'Replace Photo' : 'Upload Photo'}</span>
                </div>
              </Label>
              <Input
                id="owner_photo_upload"
                type="file"
                accept=".jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleFranchiseFileUpload(e.target.files[0], 'owner_photo');
                  }
                }}
              />
              {franchiseData.owner_photo && (
                <div className="flex items-center gap-2">
                  <img 
                    src={franchiseData.owner_photo.data} 
                    alt="Owner" 
                    className="w-12 h-12 rounded-full object-cover border"
                  />
                  <span className="text-sm text-green-600">{franchiseData.owner_photo.name}</span>
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Max file size: 10MB (JPG, PNG)</p>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Upload Documents (Optional) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Upload Documents
          </CardTitle>
          <CardDescription>
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-3 py-2 rounded-lg mt-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">These documents can be uploaded later once onboarded</span>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'franchise_agreement_doc', label: 'Franchise Agreement' },
              { key: 'fssai_license_doc', label: 'FSSAI License' },
              { key: 'trade_license_doc', label: 'Trade License' },
              { key: 'gst_certificate_doc', label: 'GST Certificate', conditional: franchiseData.is_gst_applicable }
            ].filter(doc => doc.conditional === undefined || doc.conditional).map(doc => (
              <div key={doc.key} className="flex items-center justify-between p-3 border rounded-lg">
                <span className="text-sm">{doc.label}</span>
                <div className="flex items-center gap-2">
                  {franchiseData[doc.key] && <CheckCircle className="w-5 h-5 text-green-600" />}
                  <Label htmlFor={`doc-${doc.key}`} className="cursor-pointer">
                    <Button variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        {franchiseData[doc.key] ? 'Replace' : 'Upload'}
                      </span>
                    </Button>
                  </Label>
                  <Input
                    id={`doc-${doc.key}`}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        handleFranchiseFileUpload(e.target.files[0], doc.key);
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3">Max file size: 10MB per file (PDF, JPG, PNG)</p>
        </CardContent>
      </Card>

      {/* Franchise Credentials Preview */}
      {createdFranchise && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            <strong>Franchise Created!</strong><br />
            Franchise ID: <code className="bg-green-100 px-2 py-1 rounded">{createdFranchise.franchise_id}</code><br />
            PIN: <code className="bg-green-100 px-2 py-1 rounded">{createdFranchise.pin}</code>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  // Step 4: Preview & Go Live
  const renderPreview = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Review & Go Live</h2>
        <p className="text-gray-600 mt-2">Review your setup before going live</p>
      </div>

      {/* Business Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Business Profile
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            {logoPreview && (
              <img src={logoPreview} alt="Logo" className="w-16 h-16 object-contain rounded border" />
            )}
            <div className="space-y-2 flex-1">
              <p className="font-semibold text-lg">{businessProfile.brand_name}</p>
              <p className="text-gray-600">{businessProfile.company_name}</p>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-500 pt-2 border-t">
                <div>
                  <span className="font-medium">Location:</span> {businessProfile.city}, {businessProfile.state}
                </div>
                <div>
                  <span className="font-medium">Pincode:</span> {businessProfile.pincode || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {businessProfile.contact_email || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Phone:</span> {businessProfile.contact_phone || 'N/A'}
                </div>
                {businessProfile.gstin && (
                  <div className="col-span-2">
                    <span className="font-medium">GSTIN:</span> {businessProfile.gstin}
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Menu Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Menu Items ({menuItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {menuItems.length === 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                At least 1 menu item is required to go live.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {/* Group by category */}
              {Object.entries(
                menuItems.reduce((acc, item) => {
                  const cat = item.category || 'Uncategorized';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(item);
                  return acc;
                }, {})
              ).map(([category, items]) => (
                <div key={category} className="border rounded-lg p-3">
                  <p className="font-medium text-sm text-amber-700 mb-2">{category}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {items.slice(0, 4).map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                        <span>{item.name}</span>
                        <span className="font-medium">₹{item.price}</span>
                      </div>
                    ))}
                    {items.length > 4 && (
                      <p className="text-xs text-gray-500 col-span-2">+{items.length - 4} more items</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Franchise Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Store className="w-5 h-5" />
            First Franchise
          </CardTitle>
        </CardHeader>
        <CardContent>
          {createdFranchise ? (
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-lg">{franchiseData.outlet_name}</p>
                  <p className="text-sm text-gray-500">Owner: {franchiseData.owner_full_name || 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">Franchise ID</p>
                  <code className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-sm font-mono">{createdFranchise.franchise_id}</code>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 pt-2 border-t">
                <div>
                  <span className="font-medium">Address:</span><br/>
                  {franchiseData.outlet_full_address}
                </div>
                <div>
                  <span className="font-medium">Location:</span><br/>
                  {franchiseData.outlet_city}, {franchiseData.outlet_district}, {franchiseData.outlet_state} - {franchiseData.outlet_pincode}
                </div>
                <div>
                  <span className="font-medium">Owner Phone:</span> {franchiseData.owner_phone || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Owner Email:</span> {franchiseData.owner_email || 'N/A'}
                </div>
                {franchiseData.is_gst_applicable && (
                  <div className="col-span-2">
                    <span className="font-medium">GSTIN:</span> {franchiseData.outlet_gstin || 'N/A'}
                  </div>
                )}
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded p-3 mt-2">
                <p className="text-sm font-medium text-green-800">Login Credentials</p>
                <div className="grid grid-cols-2 gap-2 mt-1 text-sm">
                  <div><span className="text-green-600">Franchise ID:</span> <code className="font-mono">{createdFranchise.franchise_id}</code></div>
                  <div><span className="text-green-600">PIN:</span> <code className="font-mono">{createdFranchise.pin}</code></div>
                </div>
              </div>
            </div>
          ) : franchiseData.outlet_name ? (
            <div className="space-y-2">
              <p className="font-semibold">{franchiseData.outlet_name}</p>
              <p className="text-sm text-gray-600">
                {franchiseData.outlet_full_address}, {franchiseData.outlet_city}
              </p>
              <Alert className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Franchise data entered but not yet saved. Go back to Step 3 to create the franchise.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <p className="text-gray-500">Franchise not yet created</p>
          )}
        </CardContent>
      </Card>

      {/* Go Live Requirements */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <h4 className="font-semibold mb-2">Requirements to Go Live:</h4>
          <ul className="space-y-1 text-sm">
            <li className="flex items-center gap-2">
              {businessProfile.brand_name ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-red-600" />
              )}
              Business profile complete
            </li>
            <li className="flex items-center gap-2">
              {menuItems.length > 0 ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-red-600" />
              )}
              At least 1 menu item
            </li>
            <li className="flex items-center gap-2">
              {createdFranchise || franchiseData.outlet_name ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-red-600" />
              )}
              First franchise configured
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/fos-logo.png" alt="FOS" className="h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-gray-900">Welcome to FOS!</h1>
          <p className="text-gray-600 mt-2">Let&apos;s set up your franchise management system</p>
        </div>

        {/* Progress */}
        {renderProgress()}

        {/* Step Content */}
        <Card>
          <CardContent className="p-6">
            {currentStep === 1 && renderBusinessProfile()}
            {currentStep === 2 && renderMenuSetup()}
            {currentStep === 3 && renderFranchiseSetup()}
            {currentStep === 4 && renderPreview()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1 || saving}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          <div className="flex gap-2">
            {currentStep === 2 && (
              <Button variant="ghost" onClick={skipMenuSetup} disabled={saving}>
                Skip for now
              </Button>
            )}

            {currentStep < 4 ? (
              <Button onClick={nextStep} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {currentStep === 3 ? 'Create & Continue' : 'Save & Continue'}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button 
                onClick={goLive} 
                disabled={saving || menuItems.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Go Live!
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
